import cloneDeep from 'lodash/cloneDeep';
import IntervalTree from 'node-interval-tree';
import { getUpdatedTimedTranscript } from './transcriptUtils';
import { LINE_BREAK_CHARACTER } from './transcriptTextConstants';
import TranscriptMarker from './transcriptMarker';
import type { Paragraph, SelectedWord } from './transcriptParagraph';
import TranscriptParagraph, { findWordRangeFromParagraph } from './transcriptParagraph';
import type TranscriptParametricRule from './transcriptParametricRule';
import { isTranscriptParametricSliderRule } from './transcriptParametricRule';
import type TranscriptWord from './transcriptWord';
import Subtitle from './subtitle';
import {
  getMergedRuleSettings,
  getParametricRules,
  ParagraphRuleProperties,
  paragraphRulesSettings,
  SpeakerRuleProperties,
  speakerRulesSettings,
  SubtitleRuleProperties,
  subtitleRulesSettings,
} from './transcriptParametricRules';
import type { RuleSettingOverride } from './ruleSettingOverride';
import type { LineBreakIndex } from './lineBreakMarker';
import LineBreakMarker, { isInstanceOfLineBreakMarker } from './lineBreakMarker';
import type { TenantConfigDto } from '../dto/tenantModel';
import { safeArrayAccess } from '../util/safeArrayAccess';

export enum TranscriptViewType {
  Paragraphs,
  Subtitles,
}

export interface Transcript {
  paragraphs: Paragraph[];
}

export const millisecondsToVttTime = (milliseconds: number) => {
  let seconds = milliseconds / 1000.0;
  let hours = 0;
  let minutes = 0;
  hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  minutes = Math.floor(seconds / 60);
  const secondsString = (seconds - minutes * 60).toFixed(3);

  const result =
    hours.toString().padStart(2, '0') +
    ':' +
    minutes.toString().padStart(2, '0') +
    ':' +
    secondsString.padStart(6, '0');

  return result;
};

const supportedFrameRates: ReadonlySet<number> = new Set([23.976, 24, 25, 29.97, 30, 50, 59.94, 60]);
const MINIMUM_SUBTITLE_CUE_DURATION_IN_MS = 1500;

function cloneTranscriptMarker(marker: TranscriptMarker): TranscriptMarker {
  if (isInstanceOfLineBreakMarker(marker)) {
    return new LineBreakMarker(marker.p);
  }
  return new TranscriptMarker(marker.p);
}

function cloneTranscriptMarkers(markers: readonly TranscriptMarker[]): TranscriptMarker[] {
  return markers.map((marker: TranscriptMarker) => cloneTranscriptMarker(marker));
}

function areEquivalentTranscriptMarkers(a: TranscriptMarker, b: TranscriptMarker): boolean {
  return a.p === b.p && isInstanceOfLineBreakMarker(a) === isInstanceOfLineBreakMarker(b);
}

function appendMarkersWithoutAdjacentDuplicates(
  prefix: readonly TranscriptMarker[],
  suffix: readonly TranscriptMarker[],
): TranscriptMarker[] {
  const merged = [...prefix];
  for (const marker of suffix) {
    const previous = merged.at(-1);
    if (previous !== undefined && areEquivalentTranscriptMarkers(previous, marker)) {
      continue;
    }
    merged.push(marker);
  }
  return merged;
}

/**
 * A time-aligned transcript segmented into paragraphs and subtitle cues.
 *
 * ## Markers
 *
 * A marker is a word-array index (`p`) at which a new paragraph or subtitle cue
 * begins. The segment `[previousMarker.p, marker.p)` forms one paragraph or cue.
 * The final marker always equals `words.length`, capping the last segment.
 * `LineBreakMarker` is a marker subtype that inserts a visual line break within
 * a cue rather than starting a new one.
 *
 * ## Marker validity model
 *
 * `paragraphMarkers` and `subtitleMarkers` are two independent segmentation
 * tracks over the same flattened word timeline.
 *
 * - `paragraphMarkers` is authoritative when paragraph mode is active, or after
 *   paragraph-targeted updates (`updateParagraphMarkers`, paragraph-mode
 *   split/merge/edit operations, or `ingestMoreWords` while paragraph mode is
 *   active).
 * - `subtitleMarkers` is authoritative when subtitle mode is active, or after
 *   subtitle-targeted updates (`updateSubtitleMarkers`, subtitle-mode
 *   split/merge/edit operations, or `ingestMoreWords` while subtitle mode is
 *   active).
 *
 * The non-active track is preserved but can lag the newest tail words until
 * that track is explicitly advanced or re-derived from active rules.
 * `paragraphs` always reflects whichever track was most recently used to build
 * the current view.
 *
 * ## Working state and baseline
 *
 * Two parallel states are maintained:
 * - Working state: `paragraphMarkers`, `subtitleMarkers`, and the word array as
 *   presented in the editor. User structural edits (split, merge, marker moves)
 *   modify this state only.
 * - Unedited baseline: the transcript and segmentation as delivered by the
 *   transcription pipeline. `revert()` restores the working state to this
 *   baseline.
 *
 * The baseline keeps paragraph and subtitle marker tracks separately as well;
 * each baseline track advances only when explicitly updated (`setUneditedTranscript`
 * or `ingestMoreWords` while that track's mode is active).
 *
 * `insertParagraphAfter` creates zero-word paragraphs as transient editor
 * state. Callers are responsible for removing them if they remain empty when
 * the user commits or cancels.
 */
export default class TimedTranscript implements Transcript {
  private _paragraphs: TranscriptParagraph[] = [];
  private _transcriptViewType: TranscriptViewType = TranscriptViewType.Paragraphs;
  private paragraphIntervalTree!: any;
  private uneditedParagraphMarkers: TranscriptMarker[];
  private uneditedSubtitleMarkers: TranscriptMarker[];
  private uneditedTimedTranscript: any[];
  public timecode?: string;
  public mediaFrameRate?: number;

  public paragraphMarkers: TranscriptMarker[] = [];
  public subtitleMarkers: TranscriptMarker[] = [];

  public uneditedParagraphRules: TranscriptParametricRule[] = [];
  public uneditedSubtitleRules: TranscriptParametricRule[] = [];
  public uneditedSpeakerRules: TranscriptParametricRule[] = [];

  public paragraphRules: TranscriptParametricRule[] = [];
  public subtitleRules: TranscriptParametricRule[] = [];
  public speakerRules: TranscriptParametricRule[] = [];

  /**
   * Interval tree keeps a reference to all the paragraphs, including all the words inside the paragraph object.
   * These references prevent the VueComponents from cleaning up properly so we have to null them manually.
   */
  public cleanup() {
    for (const p of this._paragraphs) {
      p.cleanup();
    }
    this.paragraphIntervalTree = undefined;
  }

  public constructor(
    timedTranscript: any[],
    paragraphMarkers: TranscriptMarker[] = [],
    subtitleMarkers: TranscriptMarker[] = [],
    paragraphRules: TranscriptParametricRule[] = [],
    subtitleRules: TranscriptParametricRule[] = [],
    timecode?: string,
    mediaFrameRate?: number,
    showParagraphView = true,
    // Defaulted only because TS forbids a required parameter after the
    // already-optional `showParagraphView`. All v2 callers pass an explicit
    // array; v1 callers (Transcript.vue, the cutter) pass `[]`.
    speakerRules: TranscriptParametricRule[] = []
  ) {

    /*
      Shallow per-word copy is enough: Word fields are all primitives (see
      transcriptWord.ts:Word). cloneDeep was ~5× slower because it walks
      arbitrary nesting; on a multi-thousand-word live transcript that's tens of
      ms of synchronous main-thread work per poll. The copies stay independent
      of `paragraphs` so the in-place mutations the editor performs (e.g.
      backfilling speakers) don't bleed into `revert()`.
    */
    this.uneditedTimedTranscript = timedTranscript.map((w: TranscriptWord) => ({ ...w }));

    this.paragraphMarkers = cloneTranscriptMarkers(paragraphMarkers);
    this.subtitleMarkers = cloneTranscriptMarkers(subtitleMarkers);

    this.paragraphRules = paragraphRules;
    this.subtitleRules = subtitleRules;
    this.speakerRules = speakerRules;

    if (!paragraphMarkers || paragraphMarkers.length === 0) {
      const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeParagraphRules.length > 0) {
        this.paragraphMarkers = TimedTranscript.getMarkersFromRules(timedTranscript, this.paragraphRules);
      }
    } else {
      this.disableParagraphRules();
    }

    if (!subtitleMarkers || subtitleMarkers.length === 0) {
      const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeSubtitleRules.length > 0 && subtitleMarkers.length === 0) {
        this.subtitleMarkers = TimedTranscript.getMarkersFromRules(timedTranscript, this.subtitleRules);
      }
    } else {
      this.disableSubtitleRules();
    }

    this.uneditedParagraphRules = cloneDeep(this.paragraphRules);
    this.uneditedSubtitleRules = cloneDeep(this.subtitleRules);
    this.uneditedSpeakerRules = cloneDeep(this.speakerRules);
    this.uneditedParagraphMarkers = cloneTranscriptMarkers(this.paragraphMarkers);
    this.uneditedSubtitleMarkers = cloneTranscriptMarkers(this.subtitleMarkers);

    if (mediaFrameRate && supportedFrameRates.has(mediaFrameRate)) {
      this.timecode = timecode;
      this.mediaFrameRate = mediaFrameRate;
    }

    if (showParagraphView) {
      this._transcriptViewType = TranscriptViewType.Paragraphs;
      this.paragraphs = this.createParagraphs(timedTranscript, this.paragraphMarkers);
    } else {
      this._transcriptViewType = TranscriptViewType.Subtitles;
      this.paragraphs = this.createParagraphs(timedTranscript, this.subtitleMarkers);
    }
  }

  static convertToVtt(timedTranscript: TranscriptWord[], markers: TranscriptMarker[], wordSeparator: string): string {
    const segments: TranscriptWord[][] = [];
    let line = [];
    let start: number = 0;
    let end: number = 0;
    const lineBreakIndex: LineBreakIndex = {};
    for (const marker of markers) {
      if (isInstanceOfLineBreakMarker(marker)) {
        lineBreakIndex[marker.p] = true;
        continue;
      }
      end = marker.p;
      line = timedTranscript.slice(start, end);
      start = end;
      if (line.length > 0) {
        segments.push(line);
      }
    }

    let vttFileContents = 'WEBVTT\n\n';
    let wordIndex = -1;
    segments.forEach((s: TranscriptWord[], i: number) => {
      const currentSegmentStartTime = safeArrayAccess(s, 0)?.startTime ?? 0;
      let currentSegmentEndTime = safeArrayAccess(s, s.length - 1)?.endTime ?? 0;
      const currentSegmentDuration = currentSegmentEndTime - currentSegmentStartTime;

      const nextSegment = segments[i + 1];
      if (nextSegment && nextSegment[0] && currentSegmentDuration < MINIMUM_SUBTITLE_CUE_DURATION_IN_MS) {
        const diff = MINIMUM_SUBTITLE_CUE_DURATION_IN_MS - currentSegmentDuration;
        currentSegmentEndTime = currentSegmentEndTime + diff > nextSegment[0].startTime ? nextSegment[0].startTime : currentSegmentEndTime + diff;
      }

      vttFileContents += `${i + 1}\n`;
      vttFileContents += `${millisecondsToVttTime(currentSegmentStartTime)} --> ${millisecondsToVttTime(currentSegmentEndTime - 1)}\n`;
      s.forEach((t: TranscriptWord, index: number) => {
        if (t.content?.trim() === LINE_BREAK_CHARACTER) {
          return;
        }
        wordIndex++;
        return vttFileContents += `${t.content}${wordSeparator}${(index !== s.length - 1 && lineBreakIndex[wordIndex]) ? '\n' : ''}`;
      });
      vttFileContents += '\n\n';
    });

    return vttFileContents;
  }

  static getMarkersFromRules(timedTranscript: TranscriptWord[], rules: TranscriptParametricRule[]): TranscriptMarker[] {
    const subtitle = new Subtitle();
    const lineBreakCue = new Subtitle();
    const markers: TranscriptMarker[] = [];
    let marker = 0;

    for (let transcriptIndex = 0; transcriptIndex < timedTranscript.length; transcriptIndex++) {
      const cur = safeArrayAccess(timedTranscript, transcriptIndex);
      if (transcriptIndex !== 0) {
        const prev = safeArrayAccess(timedTranscript, transcriptIndex - 1);
        let segmentationRuleTriggered = false;
        let lineBreakRuleTriggered = false;
        for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
          const rule = safeArrayAccess(rules, ruleIndex);
          if (rule.isActive) {
            if (rule.property === String(SubtitleRuleProperties.LINE_BREAK_MAX_CHARACTERS)) {
              if (rule.checkRule(lineBreakCue.currentCue, prev, cur)) {
                lineBreakRuleTriggered = true;
              }
              continue;
            }
            if (!segmentationRuleTriggered) {
              if (rule.checkRule(subtitle.currentCue, prev, cur)) {
                segmentationRuleTriggered = true;
              }
            }

            if (lineBreakRuleTriggered && segmentationRuleTriggered) {
              break;
            }
          }
        }

        if (segmentationRuleTriggered) {
          marker += subtitle.currentCue.words.length;
          markers.push(new TranscriptMarker(marker));
          subtitle.addNewCue();
          lineBreakCue.addNewCue();
        } else if (lineBreakRuleTriggered) {
          markers.push(new LineBreakMarker(transcriptIndex - 1));
          lineBreakCue.addNewCue();
        }
      }
      subtitle.addWordToCurrentCue(cur);
      lineBreakCue.addWordToCurrentCue(cur);
    }
    // Adding last marker
    markers.push(new TranscriptMarker(timedTranscript.length));
    return markers;
  }

  public areParagraphRulesActive(): boolean {
    return this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive).length > 0;
  }

  public areSubtitleRulesActive(): boolean {
    return this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive).length > 0;
  }

  public areSpeakerRulesActive(): boolean {
    return this.speakerRules.filter((rule: TranscriptParametricRule) => rule.isActive).length > 0;
  }

  /**
   * Baseline paragraph segmentation before user-initiated structural edits.
   *
   * Updated by the constructor, `setUneditedTranscript`, and by
   * `ingestMoreWords` while paragraph mode is active. Calls made while subtitle
   * mode is active do not mutate this baseline track.
   */
  public getUneditedParagraphMarkers(): readonly TranscriptMarker[] {
    return cloneTranscriptMarkers(this.uneditedParagraphMarkers);
  }

  /**
   * Baseline subtitle segmentation before user-initiated structural edits.
   *
   * Updated by the constructor, `setUneditedTranscript`, and by
   * `ingestMoreWords` while subtitle mode is active. Calls made while paragraph
   * mode is active do not mutate this baseline track.
   */
  public getUneditedSubtitleMarkers(): readonly TranscriptMarker[] {
    return cloneTranscriptMarkers(this.uneditedSubtitleMarkers);
  }

  public disableActiveViewRules() {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.disableParagraphRules();
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.disableSubtitleRules();
    }
  }

  public disableParagraphRules() {
    this.paragraphRules = this.paragraphRules.map((rule: TranscriptParametricRule) => {
      return { ...rule, isActive: false };
    });
  }

  public disableSubtitleRules() {
    this.subtitleRules = this.subtitleRules.map((rule: TranscriptParametricRule) => {
      return { ...rule, isActive: false };
    });
  }

  public getParagraphRulesParameters(): Record<string, RuleSettingOverride> {
    return this.getRulesParameters(this.paragraphRules);
  }

  public getSubtitleRulesParameters(): Record<string, RuleSettingOverride> {
    return this.getRulesParameters(this.subtitleRules);
  }

  public getSpeakerRulesParameters(): Record<string, RuleSettingOverride> {
    return this.getRulesParameters(this.speakerRules);
  }

  private getRulesParameters(rules: TranscriptParametricRule[]): Record<string, RuleSettingOverride> {
    const result: Record<string, RuleSettingOverride> = {};
    for (const rule of rules) {
      result[rule.property] = isTranscriptParametricSliderRule(rule)
        ? { isActive: rule.isActive, value: rule.value }
        : { isActive: rule.isActive };
    }
    return result;
  }

  public updateParagraphMarkers(m: readonly TranscriptMarker[]) {
    const clonedMarkers = cloneTranscriptMarkers(m);
    this.paragraphMarkers = clonedMarkers;
    this._transcriptViewType = TranscriptViewType.Paragraphs;
    this.paragraphs = this.createParagraphs(this.flatten(), clonedMarkers);
  }

  public updateSubtitleMarkers(m: readonly TranscriptMarker[]) {
    const clonedMarkers = cloneTranscriptMarkers(m);
    this.subtitleMarkers = clonedMarkers;
    this._transcriptViewType = TranscriptViewType.Subtitles;
    this.paragraphs = this.createParagraphs(this.flatten(), clonedMarkers);
  }

  get paragraphs() {
    return this._paragraphs;
  }

  set paragraphs(paragraphs: TranscriptParagraph[]) {
    this._paragraphs = paragraphs;
    this.buildParagraphIntervalTree();
  }

  get transcriptViewType(): TranscriptViewType {
    return this._transcriptViewType;
  }

  set transcriptViewType(transcriptViewType: TranscriptViewType) {
    this._transcriptViewType = transcriptViewType;
    const tt = this.flatten();
    if (transcriptViewType === TranscriptViewType.Paragraphs) {
      let markers = this.paragraphMarkers;
      const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeParagraphRules.length > 0) {
        markers = TimedTranscript.getMarkersFromRules(tt, this.paragraphRules);
      }
      this.paragraphs = this.createParagraphs(tt, markers);
    } else if (transcriptViewType === TranscriptViewType.Subtitles) {
      let markers = this.subtitleMarkers;
      const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeSubtitleRules.length > 0) {
        markers = TimedTranscript.getMarkersFromRules(tt, this.subtitleRules);
      }
      this.paragraphs = this.createParagraphs(tt, markers);
    }
  }

  public addParagraph(paragraph: TranscriptParagraph, reindex: boolean = true) {
    this._paragraphs.push(paragraph);
    if (reindex) {
      this.buildParagraphIntervalTree();
    }
  }

  public getParagraphByTime(time: number): TranscriptParagraph | undefined {
    let paragraph = undefined;
    for (let i = 0; i < this.paragraphs.length; i++) {
      const p = this.paragraphs[i];
      if (!p) {
        continue;
      }
      if (time >= p.startTime && p.endTime > time) {
        paragraph = p;
        break;
      }
    }
    return paragraph;
  }

  public getIndexOfParagraph(paragraph: TranscriptParagraph): number {
    return this.paragraphs.indexOf(paragraph);
  }

  /**
   * Last word's `endTime` across all paragraphs, or `undefined` for an empty
   * transcript. Scans backward over trailing empty paragraphs only, so callers
   * can use it as a watermark without flattening or comparing the full word
   * array.
   */
  public getLastWordEndTimeMs(): number | undefined {
    for (let idx = this._paragraphs.length - 1; idx >= 0; idx -= 1) {
      const endTime = this._paragraphs[idx]?.words.at(-1)?.endTime;
      if (endTime !== undefined) {
        return endTime;
      }
    }
    return undefined;
  }

  public setUneditedTranscript(
    timedTranscript: any[],
    paragraphMarkers: TranscriptMarker[],
    subtitleMarkers: TranscriptMarker[],
    paragraphRules: TranscriptParametricRule[],
    subtitleRules: TranscriptParametricRule[]
  ) {
    /*
      Shallow per-word copy for the same reason as the constructor: Word is a
      flat primitive record, so spread is enough.

      Marker and rule arrays are deep-cloned rather than spread because the
      caller retains references to the same objects and may mutate marker
      positions or rule fields (isActive, value) in place. cloneDeep ensures the
      stored snapshot is independent of those future mutations regardless of how
      the caller manages its copies.
    */
    this.uneditedTimedTranscript = timedTranscript.map((w: TranscriptWord) => ({ ...w }));
    this.uneditedParagraphMarkers = cloneTranscriptMarkers(paragraphMarkers);
    this.uneditedSubtitleMarkers = cloneTranscriptMarkers(subtitleMarkers);
    this.uneditedParagraphRules = cloneDeep(paragraphRules);
    this.uneditedSubtitleRules = cloneDeep(subtitleRules);
  }

  public splitParagraph(cur: TranscriptParagraph, characterOffset: number, wordSeparator = ' '): boolean {
    const raw = cur.getRaw();
    const first = raw.substring(0, characterOffset);
    const second = raw.substring(characterOffset);

    // New paragraphs needs at least one word
    if (!first || !second) {
      return false;
    }

    const splitSelection = { startOffset: characterOffset, endOffset: characterOffset };
    const range = findWordRangeFromParagraph(splitSelection, cur, wordSeparator);
    const curWord = safeArrayAccess(range, 0).timed;
    const wordIndex = cur.words.indexOf(curWord);

    // Need at least on word
    const remainingWords = cur.words.slice(0, wordIndex === 0 ? 1 : wordIndex);
    const remainingWordsParagraph = new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate, undefined);
    remainingWordsParagraph.update(first);

    const newParagraphWords = cur.getSubWords(wordIndex);
    const newParagraph = new TranscriptParagraph(newParagraphWords, this.timecode, this.mediaFrameRate, undefined);
    newParagraph.update(second);

    const indexOfCurrentParagraph = this.paragraphs.indexOf(cur);
    this._paragraphs = [
      ...this.paragraphs.slice(0, indexOfCurrentParagraph),
      remainingWordsParagraph,
      newParagraph,
      ...this.paragraphs.slice(indexOfCurrentParagraph + 1),
    ];

    this.buildParagraphIntervalTree();
    this.updateMarkers();
    return true;
  }

  private assertExistingParagraphIndex(idx: number, caller: 'insertParagraphAfter' | 'removeParagraphAt'): void {
    if (!Number.isInteger(idx)) {
      throw new Error(`TimedTranscript.${caller} expected an integer paragraph index, received ${idx}.`);
    }
    if (idx < 0 || idx >= this._paragraphs.length) {
      throw new Error(`TimedTranscript.${caller} index ${idx} is out of bounds for ${this._paragraphs.length} paragraphs.`);
    }
  }

  private getFiniteWordEndTimeAtOrBefore(paragraphIndex: number): number | undefined {
    for (let idx = paragraphIndex; idx >= 0; idx -= 1) {
      const endTime = this._paragraphs[idx]?.words.at(-1)?.endTime;
      if (typeof endTime === 'number' && Number.isFinite(endTime)) {
        return endTime;
      }
    }
    return undefined;
  }

  private getFiniteWordStartTimeAtOrAfter(paragraphIndex: number): number | undefined {
    for (let idx = paragraphIndex; idx < this._paragraphs.length; idx += 1) {
      const startTime = this._paragraphs[idx]?.words[0]?.startTime;
      if (typeof startTime === 'number' && Number.isFinite(startTime)) {
        return startTime;
      }
    }
    return undefined;
  }

  /**
   * Inserts a zero-word paragraph immediately after the paragraph at `idx`.
   * Throws when `idx` is not an existing paragraph index.
   */
  public insertParagraphAfter(idx: number): void {
    this.assertExistingParagraphIndex(idx, 'insertParagraphAfter');

    const previousEndTime = this.getFiniteWordEndTimeAtOrBefore(idx);
    const nextStartTime = this.getFiniteWordStartTimeAtOrAfter(idx + 1);

    const startTime = previousEndTime ?? nextStartTime ?? 0;
    const rawEndTime = nextStartTime ?? startTime;
    const endTime = Math.max(rawEndTime, startTime);

    const empty = new TranscriptParagraph([], this.timecode, this.mediaFrameRate, undefined);
    empty.setRetimingBounds(startTime, endTime);

    this._paragraphs = [
      ...this._paragraphs.slice(0, idx + 1),
      empty,
      ...this._paragraphs.slice(idx + 1),
    ];
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  /**
   * Removes the paragraph at `idx`.
   * Throws when `idx` is not an existing paragraph index.
   */
  public removeParagraphAt(idx: number): void {
    this.assertExistingParagraphIndex(idx, 'removeParagraphAt');

    this._paragraphs = [
      ...this._paragraphs.slice(0, idx),
      ...this._paragraphs.slice(idx + 1),
    ];
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  public mergeParagraphs(indexOfCurrentParagraph: number, indexOfTargetParagraph: number) {
    const target = safeArrayAccess(this.paragraphs, indexOfTargetParagraph);
    const current = safeArrayAccess(this.paragraphs, indexOfCurrentParagraph);
    const mergedParagraphWords = [...target.words, ...current.words];
    const newParagraph = new TranscriptParagraph(mergedParagraphWords, this.timecode, this.mediaFrameRate, undefined);
    const updatedParagraphs = this._paragraphs.filter((p: TranscriptParagraph, index: number) => indexOfCurrentParagraph !== index);
    this._paragraphs = [
      ...updatedParagraphs.slice(0, indexOfTargetParagraph),
      newParagraph,
      ...updatedParagraphs.slice(indexOfTargetParagraph + 1),
    ];
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  /**
   * Appends newly-streamed source words to the tail of the transcript.
   * Extends both the working state and the unedited baseline — see class doc.
   *
   * The active marker/rule track is selected from `transcriptViewType` so the
   * transcript model remains self-consistent with whichever segmentation is
   * currently rendered in `paragraphs`.
   */
  public ingestMoreWords(newWords: TranscriptWord[]): void {
    if (newWords.length === 0) {
      return;
    }

    const oldWords = this.flatten();
    const oldCount = oldWords.length;
    const combined = oldWords.concat(newWords);

    /*
      Shallow per-word copy for the same reason as the constructor: Word is a
      flat primitive record, so spread is enough to keep the unedited snapshot
      independent of any later in-place edits the editor makes.
    */
    this.uneditedTimedTranscript = this.uneditedTimedTranscript.concat(newWords.map(w => ({ ...w })));

    const isSubtitleMode = this.transcriptViewType === TranscriptViewType.Subtitles;
    const rules = isSubtitleMode ? this.subtitleRules : this.paragraphRules;
    const anyActive = rules.some((r: TranscriptParametricRule) => r.isActive);

    /*
      The working markers carry the user's edits: the baseline markers are the
      pre-edit segmentation. Both are extended from the same newly-derived tail
      so the appended source words land in the baseline as well as the live
      view.
    */
    const currentMarkers = isSubtitleMode ? this.subtitleMarkers : this.paragraphMarkers;
    const baselineMarkers = isSubtitleMode ? this.uneditedSubtitleMarkers : this.uneditedParagraphMarkers;
    const preservedCurrentPrefix = currentMarkers.filter((m: TranscriptMarker) => m.p < oldCount);
    const preservedBaselinePrefix = baselineMarkers.filter((m: TranscriptMarker) => m.p < oldCount);

    let derivedTailMarkers: TranscriptMarker[];
    if (anyActive) {
      /*
        Preserve user-edited segmentation in the existing prefix and only
        compute rule-driven markers from the join point forward. Re-deriving
        markers for the full combined transcript would overwrite user splits in
        the already-edited prefix whenever active rules disagree with those
        manual edits.
      */
      const derivedCombinedMarkers = TimedTranscript.getMarkersFromRules(combined, rules);
      derivedTailMarkers = derivedCombinedMarkers.filter((m: TranscriptMarker) => {
        if (m.p >= oldCount) {
          return true;
        }
        return isInstanceOfLineBreakMarker(m) && m.p === oldCount - 1;
      });
    } else {
      derivedTailMarkers = [new TranscriptMarker(combined.length)];
    }

    const newCurrentMarkers = appendMarkersWithoutAdjacentDuplicates(preservedCurrentPrefix, derivedTailMarkers);
    const newBaselineMarkers = appendMarkersWithoutAdjacentDuplicates(preservedBaselinePrefix, derivedTailMarkers);

    if (isSubtitleMode) {
      this.subtitleMarkers = newCurrentMarkers;
      this.uneditedSubtitleMarkers = cloneTranscriptMarkers(newBaselineMarkers);
    } else {
      this.paragraphMarkers = newCurrentMarkers;
      this.uneditedParagraphMarkers = cloneTranscriptMarkers(newBaselineMarkers);
    }
    this.paragraphs = this.createParagraphs(combined, newCurrentMarkers);
  }

  /**
   * Append new timed words to the tail of the transcript for the v2 editor,
   * whose view mode (paragraph/subtitle/speaker) is decoupled from
   * `transcriptViewType`: the caller supplies the rules and the marker track.
   *
   * With active rules, markers are re-derived so a long pause between the last
   * existing word and the first new word still produces a break. With rules
   * inactive, the existing internal breaks are kept and only the terminal
   * marker moves.
   */
  public appendWords(
    newWords: TranscriptWord[],
    rules: TranscriptParametricRule[],
    markerTarget: 'paragraph' | 'subtitle',
  ) {
    if (newWords.length === 0) {
      return;
    }

    const oldWords = this.flatten();
    const oldCount = oldWords.length;
    const combined = oldWords.concat(newWords);

    this.uneditedTimedTranscript = this.uneditedTimedTranscript.concat(newWords.map(w => ({ ...w })));

    const anyActive = rules.some((r: TranscriptParametricRule) => r.isActive);

    let markers: TranscriptMarker[];
    if (anyActive) {
      markers = TimedTranscript.getMarkersFromRules(combined, rules);
    } else {
      const sourceMarkers = markerTarget === 'subtitle' ? this.subtitleMarkers : this.paragraphMarkers;
      const internal = sourceMarkers.filter((m: TranscriptMarker) => m.p < oldCount);
      markers = [...internal, new TranscriptMarker(combined.length)];
    }

    if (markerTarget === 'subtitle') {
      this.subtitleMarkers = markers;
    } else {
      this.paragraphMarkers = markers;
    }
    this.paragraphs = this.createParagraphs(combined, markers);
  }

  public mergeCurrentParagraphWithNextFromOffset(current: TranscriptParagraph, characterOffset: number, wordSeparator = ' ') {
    const indexOfCurrentParagraph = this.paragraphs.indexOf(current);
    const next = this.paragraphs[indexOfCurrentParagraph + 1];
    if (!next) {
      return;
    }

    const firstPart = current.getRaw().substring(0, characterOffset);
    const rest = current.getRaw().substring(characterOffset);


    const mergeSelection = { startOffset: characterOffset, endOffset: characterOffset };
    const range = findWordRangeFromParagraph(mergeSelection, current, wordSeparator);
    const curWord = safeArrayAccess(range, 0).timed;
    let wordIndex = current.words.indexOf(curWord);

    const wordCount = firstPart.split(' ');
    if (firstPart !== '' && wordCount.length === 1) {
      wordIndex = 1;
    }

    const remainingWords = current.words.slice(0, wordIndex);
    let remainingWordsParagraph;
    if (remainingWords.length > 0) {
      remainingWordsParagraph = new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate, undefined);
      remainingWordsParagraph.update(firstPart);
    }
    const newParagraphWords = [...current.getSubWords(wordIndex), ...next.words];
    const newParagraph = new TranscriptParagraph(newParagraphWords, this.timecode, this.mediaFrameRate, undefined);
    newParagraph.update(rest + ' ' + next.getRaw());

    if (remainingWordsParagraph) {
      this._paragraphs = [
        ...this.paragraphs.slice(0, indexOfCurrentParagraph),
        remainingWordsParagraph,
        newParagraph,
        ...this.paragraphs.slice(indexOfCurrentParagraph + 2),
      ];
    } else {
      this._paragraphs = [
        ...this.paragraphs.slice(0, indexOfCurrentParagraph),
        newParagraph,
        ...this.paragraphs.slice(indexOfCurrentParagraph + 2),
      ];
    }
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  public getActiveParagraphs(startTime: number, endTime: number): { paragraph: TranscriptParagraph, words: TranscriptWord[] }[] {
    const paragraphs = this.paragraphIntervalTree.search(startTime, endTime);
    return paragraphs.map((p: TranscriptParagraph) => {
      return {
        paragraph: p,
        words: p.getWordsByTime(startTime, endTime)
      };
    });
  }

  public getRaw(): string {
    return this.paragraphs.reduce((acc: string, cur: TranscriptParagraph) => {
      return `${acc} ${cur.words.reduce((accW: string, curW: any) => `${accW} ${curW.content}`, '').trim()}`;
    }, '');
  }

  public flatten(): TranscriptWord[] {
    return this.paragraphs.flatMap((p: TranscriptParagraph) => p.words);
  }

  public update(editedText: string, markers: TranscriptMarker[]) {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphMarkers = markers;
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.subtitleMarkers = markers;
    }
    const tt = this.flatten();
    let updatedTT;
    if (!editedText.trim()) {
      // Currently the transcript editor will crash if all text is removed, and
      // fixing this looks to be non-trivial. So as a very hacky workaround before
      // this is improved, we always leave one character remaining in the editor.
      const firstTt = safeArrayAccess(tt, 0);
      updatedTT = [{
        startTime: firstTt.startTime,
        endTime: firstTt.endTime,
        content: firstTt.content[0]!,
        edited: true
      }];
    } else {
      updatedTT = getUpdatedTimedTranscript(tt, editedText);
    }
    this.paragraphs = this.createParagraphs(updatedTT, markers);
  }

  public revert() {
    // Need to make copy of the backup, so we don't lose it and edit it
    let paragraphMarkers = cloneTranscriptMarkers(this.uneditedParagraphMarkers);
    let subtitleMarkers = cloneTranscriptMarkers(this.uneditedSubtitleMarkers);
    this.paragraphRules = cloneDeep(this.uneditedParagraphRules);
    this.subtitleRules = cloneDeep(this.uneditedSubtitleRules);

    const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
    if (activeParagraphRules.length > 0) {
      paragraphMarkers = TimedTranscript.getMarkersFromRules(this.uneditedTimedTranscript, this.paragraphRules);
    }
    const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
    if (activeSubtitleRules.length > 0) {
      subtitleMarkers = TimedTranscript.getMarkersFromRules(this.uneditedTimedTranscript, this.subtitleRules);
    }
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphs = this.createParagraphs(cloneDeep(this.uneditedTimedTranscript), cloneTranscriptMarkers(paragraphMarkers));
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.paragraphs = this.createParagraphs(cloneDeep(this.uneditedTimedTranscript), cloneTranscriptMarkers(subtitleMarkers));
    }
    this.paragraphMarkers = cloneTranscriptMarkers(paragraphMarkers);
    this.subtitleMarkers = cloneTranscriptMarkers(subtitleMarkers);
  }

  private updateMarkers() {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphMarkers = this.createMarkers();
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.subtitleMarkers = this.createMarkers();
    }
  }

  private createMarkers(): TranscriptMarker[] {
    const paragraphMarkers: TranscriptMarker[] = [];
    let position = 0;
    this._paragraphs.forEach((p: TranscriptParagraph) => {
      position += p.words.length;
      paragraphMarkers.push(new TranscriptMarker(position));
    });
    return paragraphMarkers;
  }

  private createParagraphs(timedTranscript: any[], markers: readonly TranscriptMarker[]): TranscriptParagraph[] {
    const paragraphs = [];
    let pStart = 0;
    let pEnd = 0;
    let lineBreakIndex: LineBreakIndex = {};
    let nextLineBreakIndex: LineBreakIndex = {};
    for (let index = 0; index < markers.length; index++) {
      const marker = markers[index];
      if (!marker) {
        continue;
      }
      const nextMarker = markers[index + 1];
      if (isInstanceOfLineBreakMarker(marker)) {
        if (nextMarker && marker.p === nextMarker.p) {
          nextLineBreakIndex[marker.p - nextMarker.p] = true;
        } else {
          lineBreakIndex[marker.p - pStart] = true;
        }
        continue;
      }
      pEnd = marker.p;
      const words = timedTranscript.slice(pStart, pEnd);
      if (words.length > 0) {
        paragraphs.push(new TranscriptParagraph(words, this.timecode, this.mediaFrameRate, lineBreakIndex));
        lineBreakIndex = {...nextLineBreakIndex};
        nextLineBreakIndex = {};
      }
      pStart = pEnd;
    }
    if (timedTranscript.length > pEnd) {
      const remainingWords = timedTranscript.slice(pEnd);
      if (remainingWords.length > 0) {
        paragraphs.push(new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate, undefined));
      }
    }
    return paragraphs;
  }

  private buildParagraphIntervalTree() {
    const tree = new IntervalTree();
    this.paragraphs.forEach((p: TranscriptParagraph) => {
      if (p.startTime <= p.endTime) {
        tree.insert(p.startTime, p.endTime, p);
      }
    });
    this.paragraphIntervalTree = tree;
  }
}


export const getIndexOfWordByParagraph = (timedTranscript: TimedTranscript, paragraphIndex: number, word: TranscriptWord) => {
  const { paragraphs } = timedTranscript;
  const paragraph = safeArrayAccess(paragraphs, paragraphIndex);
  let indexOfWord = 0;
  if (word) {
    indexOfWord += paragraph.words.indexOf(word);
    if (paragraphIndex !== -1) {
      for (let i = 0; i < paragraphIndex; i++) {
        indexOfWord += safeArrayAccess(paragraphs, i).words.length;
      }
    }
  }

  return indexOfWord;
};


export const findSelectedWords = (
  timedTranscript: TimedTranscript,
  selection: any,
  firstParagraphIndex: number,
  lastParagraphIndex: number,
  wordSeparator = ' ',
) => {
  const selectedWords: SelectedWord[] = [];
  if (!selection || firstParagraphIndex === -1 || lastParagraphIndex === -1) {
    return selectedWords;
  }
  for (
    let i = firstParagraphIndex;
    i <= firstParagraphIndex + (lastParagraphIndex - firstParagraphIndex);
    i += 1
  ) {
    const p = timedTranscript.paragraphs[i];
    if (p) {
      const raw = p.getRaw(wordSeparator);
      let startOffset = selection.startOffset;
      let endOffset = selection.endOffset;
      if (
        i === firstParagraphIndex &&
        firstParagraphIndex !== lastParagraphIndex
      ) {
        endOffset = raw.length;
      } else if (
        i ===
          firstParagraphIndex + (lastParagraphIndex - firstParagraphIndex) &&
        firstParagraphIndex !== lastParagraphIndex
      ) {
        startOffset = 0;
      } else if (i !== firstParagraphIndex) {
        startOffset = 0;
        endOffset = raw.length;
      }
      const wordRange = findWordRangeFromParagraph(
        { startOffset: startOffset, endOffset: endOffset },
        p,
        wordSeparator,
      );
      selectedWords.push(...wordRange);
    }
  }
  return selectedWords;
};


type RuleSettingsOverridesInput = Record<string, RuleSettingOverride> | TranscriptParametricRule[] | undefined;

function normalizeRuleSettingOverrides(
  values: RuleSettingsOverridesInput,
): Record<string, RuleSettingOverride> | undefined {
  if (values === undefined || Array.isArray(values)) {
    return undefined;
  }
  return values;
}

export function handleTimedTranscript(
  timedTranscript: any,
  paragraphMarkers: any[] = [],
  subtitleMarkers: any[] = [],
  tenantConfig: TenantConfigDto,
  paragraphRulesValues: RuleSettingsOverridesInput,
  subtitleRulesValues: RuleSettingsOverridesInput,
  timecode?: string,
  mediaFrameRate?: number,
  speakerRulesValues?: RuleSettingsOverridesInput,
) {
  // Sometimes words can come with only start time and end time and no content.
  const sanitized = timedTranscript.filter((t: any) => t.content && t.content !== LINE_BREAK_CHARACTER);

  const pMarkers = paragraphMarkers.map((marker: any) => new TranscriptMarker(marker.p));
  const sMarkers = subtitleMarkers.map((marker: any) => marker.lineBreak ? new LineBreakMarker(marker.p) : new TranscriptMarker(marker.p));

  const paragraphSettings = getMergedRuleSettings(tenantConfig.paragraphParametricRules, paragraphRulesSettings, Object.values(ParagraphRuleProperties)).mergedSettings;
  const subtitleSettings = getMergedRuleSettings(tenantConfig.subtitleParametricRules, subtitleRulesSettings, Object.values(SubtitleRuleProperties)).mergedSettings;
  const speakerSettings = getMergedRuleSettings(tenantConfig.speakerParametricRules, speakerRulesSettings, Object.values(SpeakerRuleProperties)).mergedSettings;

  const normalizedSubtitleRuleValues = normalizeRuleSettingOverrides(subtitleRulesValues);
  const normalizedParagraphRuleValues = normalizeRuleSettingOverrides(paragraphRulesValues);
  const normalizedSpeakerRuleValues = normalizeRuleSettingOverrides(speakerRulesValues);

  const subtitleRules = getParametricRules(subtitleSettings, normalizedSubtitleRuleValues);
  const paragraphRules = getParametricRules(paragraphSettings, normalizedParagraphRuleValues);
  const speakerRules = getParametricRules(speakerSettings, normalizedSpeakerRuleValues);

  const frameRate = mediaFrameRate ? parseFloat(mediaFrameRate.toFixed(2)) : undefined;
  return new TimedTranscript(sanitized, pMarkers, sMarkers, paragraphRules, subtitleRules, timecode, frameRate, true, speakerRules);
}
